import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { CommissionCalculationResult } from 'utils/commissionCalculator';

interface CommissionCalculationModalProps {
  show: boolean;
  onHide: () => void;
  calculationResult: CommissionCalculationResult | null;
}

const CommissionCalculationModal: React.FC<CommissionCalculationModalProps> = ({
  show,
  onHide,
  calculationResult
}) => {
  const { t } = useTranslation();

  if (!calculationResult) {
    return (
      <Modal show={show} onHide={onHide} centered size="lg" className="commission-calculation-modal bg-body">
        <Modal.Header closeButton className="border-0 pb-0 pt-3 bg-body">
          <Modal.Title>
            <FontAwesomeIcon icon={faCalculator} className="me-2 text-warning" />
            {t('products.commission.estimatedTitle', 'Estimated Commission')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-body">
          <div className="text-center py-5">
            <div className="mb-3">
              <span className="fa-stack fa-2x">
                <FontAwesomeIcon icon={faInfoCircle} className="text-secondary opacity-25 fa-stack-2x" />
              </span>
            </div>
            <p className="fs-5 text-body">{t('products.commission.noCalculationAvailable', 'No calculation available')}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 bg-body">
          <Button variant="primary" className="px-4" onClick={onHide}>
            {t('common.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="commission-calculation-modal">
      <Modal.Header closeButton className="border-0 pb-0 pt-3 bg-body">
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faCalculator} className="me-2 text-warning" size="xl" />
          <div>
            <h4  className="mb-0 text-body">{t('products.commission.estimatedTitle', 'Estimated Commission')}</h4>
            <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
              {t('products.commission.basedOnCurrentValues', 'Based on current form values')}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3 bg-body">
        <div>
          <div className="commission-calculation-details">
            
            {/* Cards row */}
            <div className="row g-3 mb-4">
              {/* Price Information */}
              <div className="col-md-6">
                <div className="card h-100 border-0 shadow-sm bg-body">
                  <div className="card-header bg-body py-2 border-0">
                    <h5 className="mb-0 fw-bold text-body">{t('products.price.title', 'Price Information')}</h5>
                  </div>
                  <div className="card-body pb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                      <span className="text-body">{t('products.price.prix', 'Prix')}</span>
                      <span className="fw-bold text-body">{Number(calculationResult.regularPrice || 0).toFixed(2)} TND</span>
                    </div>
                    
                    {calculationResult.specialPrice && (
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                        <span className="text-body">{t('products.price.special', 'Special Price')}</span>
                        <span className={`fw-bold ${calculationResult.isSpecialPriceApplicable ? 'text-success' : 'text-muted'}`}>
                          {Number(calculationResult.specialPrice || 0).toFixed(2)} TND
                        </span>
                      </div>
                    )}
                    
                    <div className="mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-body">{t('products.commission.applicablePrice', 'Price Used for Calculation')}</span>
                        <span className="fw-bold text-primary">{Number(calculationResult.applicablePrice || 0).toFixed(2)} TND</span>
                      </div>
                      <div className="small text-muted mt-1">
                        {calculationResult.isSpecialPriceApplicable 
                          ? t('products.commission.usingSpecialPrice', 'Using special price (active promotion)')
                          : t('products.commission.usingRegularPrice', 'Using regular price')
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Commission Calculation */}
              <div className="col-md-6">
                <div className="card h-100 border-0 shadow-sm bg-body">
                  <div className="card-header bg-body py-2 border-0">
                    <h6 className="mb-0 fw-bold text-body">{t('products.commission.calculation', 'Commission Calculation')}</h6>
                  </div>
                  <div className="card-body pb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                      <span className="text-body">{t('products.commission.fixCommission', 'Fix Commission')}</span>
                      <span className="fw-bold text-body">{calculationResult.fixCommission.toFixed(2)} TND</span>
                    </div>
                    
                    {/* Fixed Commission calculation explanation */}
                    <div className="bg-body-tertiary p-3 rounded mb-3 small">
                      <p className="mb-2">
                        <span className="fw-bold">{t('products.shippingMethod', 'Shipping method')}:</span> 
                        <span className="text-secondary ms-1">{calculationResult.calculationDetails.shippingMethodName}</span>
                      </p>
                      <p className="mb-2">
                        <span className="fw">{t('products.commission.volumetricWeight', 'Volumetric weight')}:</span> 
                        <span className="text-primary ms-1">{calculationResult.volumetricWeight} kg</span>
                      </p>
                      <p className="mb-2">
                        <span className="fw">{t('products.commission.taxableWeight', 'Taxable weight')}:</span> 
                        <span className="text-primary ms-1">{calculationResult.taxableWeight} kg</span>
                      </p>
                      <p className="mb-2">
                        <span className="fw">{t('products.commission.fixValue', 'Fix Value')}:</span> 
                        <span className="text-success ms-1">{calculationResult.calculationDetails.formula} = {calculationResult.fixCommission.toFixed(2)} TND</span>
                      </p>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-body">{t('products.commission.percentCommission', 'Percent Commission')}</span>
                      <span className="fw-bold text-body">{calculationResult.percentCommission}%</span>
                    </div>
                    
                    <div className="p-2 bg-body-tertiary rounded">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">{t('products.commission.percentValue', 'Percent Value')}:</span>
                        <span className="text-success">
                          {calculationResult.percentCommission}% × {Number(calculationResult.applicablePrice).toFixed(2)} TND = {calculationResult.percentCommissionValue.toFixed(2)} TND
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Total commission summary */}
            <div className="card border-0 shadow-sm mb-2">
              <div className="card-body p-3 bg-primary-subtle">
                <div className="text-center">
                  <h6 className="mb-3 text-body fw-bold">{t('products.commission.estimatedTotal', 'Estimated Total Commission')}</h6>
                  
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <span className="text fw-bold me-1">{calculationResult.fixCommission.toFixed(2)}</span>
                      <span className="text-muted mx-2">+</span>
                      <span className="text fw-bold me-1">{calculationResult.percentCommissionValue.toFixed(2)}</span>
                      <span className="text-muted mx-2">=</span>
                      <span className="text-success fs-4 fw-bold">{calculationResult.totalCommission.toFixed(2)} TND</span>
                    </div>
                  </div>
                  
                  <div className="small text-muted mt-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                    {t('products.commission.estimateNote', 'This is an estimate based on current form values. Final commission may vary.')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 bg-body">
        <Button variant="primary" className="px-4" onClick={onHide}>
          {t('common.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommissionCalculationModal;
